"use client";

import { c as _c } from "react/compiler-runtime";
import { useId, type ReactNode } from "react";
import { T, Var } from "gt-next";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PRICING_PLANS } from "./plans-data";

/**
 * Pricing Toggle Component
 *
 * Toggle between Monthly and Yearly billing periods.
 * Uses useId() for stable Radix UI IDs to prevent hydration mismatches.
 */

interface PricingToggleProps {
  activePeriod: "Monthly" | "Yearly";
  onToggle: (period: "Monthly" | "Yearly") => void;
}

// i18n: Static lookup for billing period labels
const PERIOD_T: Record<string, ReactNode> = {
  Monthly: <T>Monthly</T>,
  Yearly: <T>Yearly</T>
};

// Calculate max savings percentage (first-month price vs yearly monthly equivalent)
const maxSavings = Math.ceil(Math.max(...PRICING_PLANS.filter(p => p.firstMonthMonthlyPrice).map(p => (parseInt(p.firstMonthMonthlyPrice!) - parseInt(p.price.Yearly)) / parseInt(p.firstMonthMonthlyPrice!) * 100)));
export default function PricingToggle(t0) {
  const $ = _c(14);
  const {
    activePeriod,
    onToggle
  } = t0;
  const tabsId = useId();
  let t1;
  if ($[0] !== onToggle) {
    t1 = value => onToggle(value as "Monthly" | "Yearly");
    $[0] = onToggle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["Monthly", "Yearly"];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <TabsList variant="pill" animatedIndicator={true} indicatorClassName="bg-white shadow-sm rounded-[4px]" className="rounded-lg bg-cream p-1">{(t2 as const).map(_temp)}</TabsList>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== activePeriod || $[5] !== t1 || $[6] !== tabsId) {
    t4 = <Tabs id={tabsId} value={activePeriod} onValueChange={t1}>{t3}</Tabs>;
    $[4] = activePeriod;
    $[5] = t1;
    $[6] = tabsId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `absolute -right-16 -top-3 rounded-full bg-green-600 px-3 py-1 text-[13px] font-semibold text-white transition-[opacity,transform,translate] duration-300 ${activePeriod === "Yearly" ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"} `;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <T>Save <Var name="percent">{maxSavings}</Var>%</T>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <div className={t5}>{t6}</div>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t7) {
    t8 = <div className="flex justify-center"><div className="relative">{t4}{t7}</div></div>;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
function _temp(period) {
  return <TabsTrigger key={period} value={period} variant="pill" className="rounded-[4px] px-5 py-2.5 text-[15px] font-semibold md:px-6 md:text-[16px]">{PERIOD_T[period]}</TabsTrigger>;
}